import { createContext, useContext, useSyncExternalStore } from "react";

export const RouteLocation = createContext<string | null>(null);

const locationEvent = "signalbill:navigate";
const positionKey = "signalbill:position";
type NavigationGuard = (proceed: () => void) => void;
let guard: NavigationGuard | null = null;
let position = 0;
let reversing = false;
let bypassPop = false;
let pendingPop: (() => void) | null = null;

// Track entries without replacing other history state. A dirty Settings form can
// restore a Back/Forward entry before asking, keeping its fields mounted.
if (typeof window !== "undefined") {
  position = window.history.state?.[positionKey] ?? 0;
  window.history.replaceState({ ...window.history.state, [positionKey]: position }, "");
  window.addEventListener("popstate", () => {
    if (reversing) {
      reversing = false;
      const proceed = pendingPop;
      pendingPop = null;
      if (proceed) guard ? guard(proceed) : proceed();
      return;
    }
    const next = window.history.state?.[positionKey] ?? 0;
    const delta = next - position;
    if (guard && delta && !bypassPop) {
      reversing = true;
      pendingPop = () => { bypassPop = true; window.history.go(delta); };
      window.history.go(-delta);
      return;
    }
    bypassPop = false;
    position = next;
    window.dispatchEvent(new Event(locationEvent));
  });
}

export function registerNavigationGuard(next: NavigationGuard) {
  guard = next;
  return () => { if (guard === next) guard = null; };
}

export function requestNavigation(proceed: () => void) {
  if (guard) guard(proceed); else proceed();
}

export function navigate(path: string, { replace = false }: { replace?: boolean } = {}) {
  if (`${window.location.pathname}${window.location.search}${window.location.hash}` === path) return;
  const proceed = () => {
    if (!replace) position += 1;
    const state = { ...window.history.state, [positionKey]: position };
    if (replace) window.history.replaceState(state, "", path);
    else window.history.pushState(state, "", path);
    window.dispatchEvent(new Event(locationEvent));
  };
  requestNavigation(proceed);
}

export function useLocationUrl(): string {
  const retained = useContext(RouteLocation);
  const live = useSyncExternalStore(
    (notify) => {
      window.addEventListener(locationEvent, notify);
      return () => window.removeEventListener(locationEvent, notify);
    },
    () => `${window.location.pathname}${window.location.search}${window.location.hash}`,
    () => "/",
  );
  return retained ?? live;
}

export function useLocationPath(): string {
  return useLocationUrl().split(/[?#]/, 1)[0] ?? "/";
}
